import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { isTokenExpired, getTokenTimeRemaining, AUTH_ERROR_EVENT } from '../utils/api';

const AuthContext = createContext(null);

// Durée avant expiration pour afficher l'avertissement (3 jours)
const WARNING_THRESHOLD_SECONDS = 3 * 24 * 60 * 60;
// Intervalle de vérification du token (30 secondes)
const CHECK_INTERVAL_MS = 30 * 1000;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionWarning, setSessionWarning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const logoutTimerRef = useRef(null);
  const checkIntervalRef = useRef(null);

  // Logout avec message optionnel
  const logout = useCallback((reason = null) => {
    // Nettoyer les timers
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
    if (checkIntervalRef.current) {
      clearInterval(checkIntervalRef.current);
      checkIntervalRef.current = null;
    }
    
    localStorage.removeItem('portalToken');
    localStorage.removeItem('portalUser');
    setUser(null);
    setSessionWarning(false);
    setTimeRemaining(null);
    
    // Stocker la raison du logout pour afficher un message sur la page de connexion
    if (reason) {
      sessionStorage.setItem('logoutReason', reason);
    }
  }, []);

  // Vérifier l'état du token
  const checkTokenStatus = useCallback(() => {
    const token = localStorage.getItem('portalToken');
    
    if (!token || isTokenExpired(token)) {
      if (user) {
        logout('session_expired');
      }
      return;
    }
    
    const remaining = getTokenTimeRemaining(token);
    setTimeRemaining(Math.floor(remaining));
    
    // Afficher un avertissement si moins de 5 minutes restantes
    if (remaining <= WARNING_THRESHOLD_SECONDS && remaining > 0) {
      setSessionWarning(true);
    } else {
      setSessionWarning(false);
    }
  }, [user, logout]);

  // Initialisation
  useEffect(() => {
    const savedUser = localStorage.getItem('portalUser');
    const token = localStorage.getItem('portalToken');
    
    if (savedUser && token) {
      // Vérifier si le token est encore valide
      if (isTokenExpired(token)) {
        logout('session_expired');
      } else {
        setUser(JSON.parse(savedUser));
      }
    }
    setLoading(false);
  }, [logout]);

  // Écouter les événements d'erreur d'authentification
  useEffect(() => {
    const handleAuthError = (event) => {
      console.log('[Auth] Erreur d\'authentification détectée:', event.detail?.reason);
      logout('session_expired');
    };
    
    window.addEventListener(AUTH_ERROR_EVENT, handleAuthError);
    return () => window.removeEventListener(AUTH_ERROR_EVENT, handleAuthError);
  }, [logout]);

  // Vérification périodique du token
  useEffect(() => {
    if (user) {
      // Vérifier immédiatement
      checkTokenStatus();
      
      // Puis vérifier toutes les 30 secondes
      checkIntervalRef.current = setInterval(checkTokenStatus, CHECK_INTERVAL_MS);
      
      return () => {
        if (checkIntervalRef.current) {
          clearInterval(checkIntervalRef.current);
        }
      };
    }
  }, [user, checkTokenStatus]);

  // Login
  const login = useCallback((userData, token) => {
    localStorage.setItem('portalToken', token);
    localStorage.setItem('portalUser', JSON.stringify(userData));
    setUser(userData);
    setSessionWarning(false);
    sessionStorage.removeItem('logoutReason');
  }, []);

  // Prolonger la session (pour futur système de refresh token)
  const refreshSession = useCallback(async () => {
    // TODO: Implémenter le refresh token côté backend
    // Pour l'instant, on redirige vers la connexion
    logout('refresh_needed');
  }, [logout]);

  return (
    <AuthContext.Provider value={{ 
      user, 
      login, 
      logout, 
      loading,
      sessionWarning,
      timeRemaining,
      refreshSession,
      checkTokenStatus
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
